import {toMercator} from '@turf/projection';
import {bbox} from '@turf/turf';
import classnames from 'classnames';
import * as geojson from 'geojson';
import React from 'react';

import {renderGeojsonSvg} from 'app/components/OrderImageryModal/ImageryPreviewImage';
import {makeFitToCanvasTransform} from 'app/utils/geoJsonUtils';

import {GridFeatureCollection, colRowToCellId} from './gridGeometry';
import cs from './styles.module.scss';
import {CellId} from './types';

interface Props {
  featureGeometry: geojson.Geometry;
  featureCollection: GridFeatureCollection;
  activeCellId: CellId | null;
  reviewedCellIds: Set<CellId>;
  /**
   * When provided, cells become clickable and call this with the clicked
   * cell's id. Omitting it keeps the minimap read-only, so the component stays
   * safe to embed anywhere without a click target.
   */
  onCellClick?: (cellId: CellId) => void;
  width?: number;
  height?: number;
  padding?: number;
}

/**
 * Pure SVG mini-map of the property + grid. Renders the property outline, each
 * grid cell, the active cell highlighted, and reviewed cells filled green.
 * Has no Mapbox or context dependency so it can be embedded anywhere.
 *
 * Projection + canvas-scaling mirrors the pattern in `FeatureMiniMap`.
 */
const GridMiniMap: React.FunctionComponent<Props> = ({
  featureGeometry,
  featureCollection,
  activeCellId,
  reviewedCellIds,
  onCellClick,
  width = 150,
  height = 150,
  padding = 4,
}) => {
  const {propertyXY, cellsXY, xyToCanvas} = React.useMemo(() => {
    // toMercator without `mutate: true` returns a fresh transformed copy;
    // mutating the inputs here would corrupt the geometry refs that the
    // main-map grid layer is reading from.
    const propertyXY = toMercator(featureGeometry) as geojson.Polygon | geojson.MultiPolygon;
    const cellsXY = toMercator(featureCollection) as GridFeatureCollection;
    // Cells are filtered to the property's bbox, so their bbox covers the
    // same area as the property's bbox in Mercator.
    const xyToCanvas = makeFitToCanvasTransform(bbox(cellsXY), width, height, padding);
    return {propertyXY, cellsXY, xyToCanvas};
  }, [featureGeometry, featureCollection, width, height, padding]);

  return (
    <svg width={width} height={height} className={cs.gridMiniMap}>
      {renderGeojsonSvg(propertyXY, xyToCanvas, {className: cs.gridMiniMapFeature})}
      {cellsXY.features.map((cell) => {
        const cellId = colRowToCellId(cell.properties.col, cell.properties.row);
        const isReviewed = reviewedCellIds.has(cellId);
        const isActive = cellId === activeCellId;
        return (
          <g key={cellId}>
            {renderGeojsonSvg(cell.geometry, xyToCanvas, {
              className: classnames(cs.gridMiniMapCell, {
                [cs.gridMiniMapCellReviewed]: isReviewed,
                [cs.gridMiniMapCellActive]: isActive,
                [cs.gridMiniMapCellClickable]: !!onCellClick,
              }),
              onClick: onCellClick ? () => onCellClick(cellId) : undefined,
            })}
          </g>
        );
      })}
    </svg>
  );
};

export default GridMiniMap;
